import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import './VideoGallery.css'

interface VideoItem {
  id: string
  title: string
  tags: string[]
  libraryPath: string
  videoUrl?: string
  webmSrc?: string
  mp4Src?: string
  soundcloudUrl?: string
  twitchUrl?: string
  youtubeUrl?: string
}

interface VideoGalleryProps {
  videos: VideoItem[]
}

const VideoGallery = ({ videos }: VideoGalleryProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [isVideoReady, setIsVideoReady] = useState(false)
  const [isVideoSwitching, setIsVideoSwitching] = useState(false)
  const progressRef = useRef<number>(0)
  const progressBarRef = useRef<HTMLDivElement>(null)
  const animationFrameRef = useRef<number | null>(null)
  const startTimeRef = useRef<number>(performance.now())
  const containerRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const isVisibleRef = useRef(true)

  // Track visibility — pause auto-advance when scrolled out of view
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisibleRef.current = entry.isIntersecting
      },
      { threshold: 0.3 }
    )
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    // Reset progress via ref (no re-render)
    progressRef.current = 0
    if (progressBarRef.current) {
      progressBarRef.current.style.transform = 'scaleX(0)'
    }

    // Cancel any existing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }

    if (videos.length === 0) return

    // Delay content fade-in
    const transitionTimer = setTimeout(() => {
      setIsTransitioning(false)
    }, 450)

    const duration = 5000 // 5 seconds
    let isActive = true
    let elapsed = 0
    let lastTime = performance.now()
    startTimeRef.current = lastTime

    const animate = (currentTime: number) => {
      if (!isActive) return

      // Only accumulate time when visible
      if (isVisibleRef.current) {
        elapsed += currentTime - lastTime
      }
      lastTime = currentTime

      const newProgress = Math.min((elapsed / duration) * 100, 100)

      // Direct DOM update — no React re-render per frame
      if (isActive && newProgress >= progressRef.current && newProgress <= 100) {
        progressRef.current = newProgress
        if (progressBarRef.current) {
          progressBarRef.current.style.transform = `scaleX(${newProgress / 100})`
        }
      }

      if (newProgress < 100 && isActive) {
        animationFrameRef.current = requestAnimationFrame(animate)
      } else if (isActive && newProgress >= 100) {
        // Only auto-advance if gallery is visible
        if (!isVisibleRef.current) {
          // Stall — recheck every 500ms until visible
          const recheckInterval = setInterval(() => {
            if (!isActive) { clearInterval(recheckInterval); return }
            if (isVisibleRef.current) {
              clearInterval(recheckInterval)
              setIsTransitioning(true)
              setIsVideoSwitching(true)
              setTimeout(() => {
                if (isActive) {
                  setCurrentIndex((prev) => (prev + 1) % videos.length)
                }
              }, 320)
            }
          }, 500)
          return
        }

        setIsTransitioning(true)
        setIsVideoSwitching(true)
        setTimeout(() => {
          if (isActive) {
            setCurrentIndex((prev) => (prev + 1) % videos.length)
          }
        }, 320)
      }
    }

    // Small delay to ensure progress starts at 0
    setTimeout(() => {
      if (isActive) {
        animationFrameRef.current = requestAnimationFrame(animate)
      }
    }, 50)

    return () => {
      isActive = false
      clearTimeout(transitionTimer)
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }
  }, [currentIndex, videos.length])

  const goToPrevious = () => {
    if (isTransitioning) return
    setIsTransitioning(true)
    setIsVideoSwitching(true)
    setCurrentIndex((prev) => (prev - 1 + videos.length) % videos.length)
  }

  const goToNext = () => {
    if (isTransitioning) return
    setIsTransitioning(true)
    setIsVideoSwitching(true)
    setCurrentIndex((prev) => (prev + 1) % videos.length)
  }

  const goToSlide = (index: number) => {
    if (isTransitioning || index === currentIndex) return
    setIsTransitioning(true)
    setIsVideoSwitching(true)
    setCurrentIndex(index)
  }

  const currentVideo = videos[currentIndex]

  return (
    <div className="video-gallery" ref={containerRef}>
      {currentVideo.webmSrc || currentVideo.mp4Src ? (
        <video
          ref={videoRef}
          className={`video-gallery-background ${isVideoReady ? 'ready' : 'is-loading'} ${isVideoSwitching ? 'switching' : ''}`}
          autoPlay
          loop
          muted
          preload="auto"
          playsInline
          webkit-playsinline=""
          disablePictureInPicture
          key={currentVideo.id}
          onLoadedData={() => {
            setIsVideoReady(true)
            setTimeout(() => setIsVideoSwitching(false), 120)
            const video = videoRef.current
            if (video) {
              video.setAttribute('playsinline', '')
              video.setAttribute('webkit-playsinline', '')
              video.play().catch(() => {
                // Autoplay was prevented, handle gracefully
              })
            }
          }}
          onCanPlay={() => setIsVideoReady(true)}
          onError={() => {
            setIsVideoSwitching(false)
            setIsVideoReady(true)
          }}
        >
          {currentVideo.webmSrc && <source src={currentVideo.webmSrc} type="video/webm" />}
          {currentVideo.mp4Src && <source src={currentVideo.mp4Src} type="video/mp4" />}
        </video>
      ) : null}
      <div className="video-gallery-preload" aria-hidden="true">
        {videos.map((video, index) => (
          index !== currentIndex && (video.webmSrc || video.mp4Src) ? (
            <video key={`preload-${video.id}`} preload="metadata" muted playsInline>
              {video.webmSrc && <source src={video.webmSrc} type="video/webm" />}
              {video.mp4Src && <source src={video.mp4Src} type="video/mp4" />}
            </video>
          ) : null
        ))}
      </div>
      <div className={`video-gallery-content ${isTransitioning ? 'transitioning' : ''}`}>
        <div className="video-label">
          {currentVideo.tags.map((tag, index) => (
            <span key={index} className="video-tag">{tag}</span>
          ))}
        </div>
        <h2 className="video-title">{currentVideo.title}</h2>
        <div className="video-links">
          {currentVideo.soundcloudUrl && (
            <a href={currentVideo.soundcloudUrl} target="_blank" rel="noopener noreferrer" className="video-link" aria-label="SoundCloud">
              <i className="fab fa-soundcloud"></i>
            </a>
          )}
          {currentVideo.youtubeUrl && (
            <a href={currentVideo.youtubeUrl} target="_blank" rel="noopener noreferrer" className="video-link" aria-label="YouTube">
              <i className="fab fa-youtube"></i>
            </a>
          )}
          {currentVideo.twitchUrl && (
            <a href={currentVideo.twitchUrl} target="_blank" rel="noopener noreferrer" className="video-link" aria-label="Twitch">
              <i className="fab fa-twitch"></i>
            </a>
          )}
        </div>
        <Link to={currentVideo.libraryPath} className="video-explore-button">
          explore
        </Link>
      </div>

      <div className="video-gallery-pagination">
        <button 
          className="pagination-arrow pagination-arrow-left" 
          onClick={goToPrevious} 
          aria-label="Previous"
          disabled={isTransitioning}
        >
          <i className="fas fa-chevron-left"></i>
        </button>

        <div className="pagination-dots">
          {videos.map((_, index) => (
            <button
              key={index}
              className={`pagination-dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
              disabled={isTransitioning}
            >
              <div className="pagination-dot-inner">
                {index === currentIndex && (
                  <div
                    className="pagination-dot-progress"
                    ref={progressBarRef}
                  ></div>
                )}
              </div>
            </button>
          ))}
        </div>

        <button
          className="pagination-arrow pagination-arrow-right"
          onClick={goToNext}
          aria-label="Next"
          disabled={isTransitioning}
        >
          <i className="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>
  )
}

export default VideoGallery
